import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDownIcon, MapPinIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { ClearableSearchInput, Column, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const searchableLocationFilterSchema = z.object({
  locations: z.array(z.string()),
});

export const SearchableLocationFilter = ({
  allLocations,
  disabled,
  isInDefaultState,
  locations,
  setLocations,
  trackingMetadata,
}: {
  allLocations: string[];
  disabled?: boolean;
  isInDefaultState: boolean;
  locations: string[];
  setLocations: (locations: string[]) => void;
  trackingMetadata?: Record<string, unknown>;
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<z.infer<typeof searchableLocationFilterSchema>>({
    defaultValues: { locations },
    resolver: zodResolver(searchableLocationFilterSchema),
  });

  const handleOpenChange = (open: boolean) => {
    setOpen(open);

    // If we are closing the dropdown here it means we haven't applied
    // the filter, so reset the form to the current locations.
    //
    // We also delay the reset slightly to avoid the reset happening
    // while the dropdown is closing.
    if (!open) {
      setTimeout(() => {
        form.reset({ locations });
        setSearchQuery("");
      }, 150);
    }
  };

  const handleSubmit = (
    data: z.infer<typeof searchableLocationFilterSchema>,
  ) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.JOB,
      "Location",
      data.locations.length > 0 ? data.locations : "None",
      trackingMetadata,
    );
    setLocations(data.locations);
    setOpen(false);
  };

  const handleClear = () => {
    mixpanelUtils.trackButtonClick(
      "Clear Location Filter",
      MIXPANEL_SCREENS.JOB,
    );
    const clearedLocations: string[] = [];
    form.reset({ locations: clearedLocations });
    setLocations(clearedLocations);
    setSearchQuery("");
    setOpen(false);
  };

  const query = searchQuery.trim().toLowerCase();
  const filteredLocations = query
    ? allLocations.filter((location) => location.toLowerCase().includes(query))
    : allLocations;

  // Need to reset the form when locations change as a result of global reset.
  useEffect(() => {
    form.reset({ locations });
  }, [form, locations]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange} open={open}>
      <DropdownMenuTrigger disabled={disabled} asChild>
        <Button
          className="rounded-full"
          variant={isInDefaultState ? "outlineSecondary" : "filterOrSortActive"}
        >
          <MapPinIcon className="h-4 w-4" />
          Location
          <ChevronDownIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[250px] p-0">
        <Form {...form}>
          <form
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="locations"
              render={() => (
                <FormItem className="p-4">
                  <FormLabel>Location</FormLabel>
                  <ClearableSearchInput
                    clearButtonClassName="right-2"
                    inputRef={searchInputRef}
                    onChange={setSearchQuery}
                    placeholder="Search location"
                    value={searchQuery}
                  />
                  <Column className="max-h-60 gap-2 overflow-y-auto">
                    {filteredLocations.length === 0 ? (
                      <p className="text-sm text-slate-500">
                        No locations found
                      </p>
                    ) : (
                      filteredLocations.map((item) => (
                        <FormField
                          key={item}
                          control={form.control}
                          name="locations"
                          render={({ field }) => {
                            return (
                              <FormItem className="flex gap-2 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value.includes(item)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        field.onChange([...field.value, item]);
                                      } else {
                                        field.onChange(
                                          field.value.filter(
                                            (value) => value !== item,
                                          ),
                                        );
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {item}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))
                    )}
                  </Column>
                </FormItem>
              )}
            />
            <Row className="items-center justify-between border-t border-slate-200 p-2">
              <Button
                className="text-slate-500 hover:text-slate-700"
                onClick={handleClear}
                type="button"
                variant="ghost"
              >
                Clear
              </Button>
              <Button variant="ghost" type="submit">
                Apply
              </Button>
            </Row>
          </form>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
